/**
 * What the app believes about the network — and how it finds out.
 *
 * `navigator.onLine` is the cheap signal, and on its own it is not enough. It
 * answers "is there a network interface?", not "can I reach the server", and on
 * iOS/WKWebView it is routinely TRUE with wifi switched off, on a captive
 * portal, or with one bar in a basement. In those states a fetch does not fail —
 * it HANGS, for as long as the platform's own socket timeout (tens of seconds).
 *
 * That gap is what made the app look frozen: the user switches wifi off in
 * Settings, comes back, taps a tab, and the client-side (RSC) navigation sits
 * waiting on a request that will never answer. No `offline` event ever fired, so
 * nothing in the app knew, and nothing offered offline mode.
 *
 * So this module layers a PROBE over `navigator.onLine`: a tiny, timeout-bounded
 * request to our own origin whose result is the authority. `isOnline()` stays
 * synchronous and cheap for all its existing callers — it just stops lying:
 *
 *     isOnline() === navigator.onLine !== false && lastProbeVerdict !== false
 *
 * A `false` verdict is STICKY: it survives until a probe succeeds. There is no
 * event to clear it (the interface never changed, so no `online` event will
 * fire), which is exactly why `startConnectivityWatch()` keeps probing.
 */

type ConnectivityListener = (online: boolean) => void;

let listeners: ConnectivityListener[] = [];
let initialized = false;

/**
 * The last probe's verdict, or `null` when we have never probed. `null` means
 * "trust `navigator.onLine`" — the app must not claim to be offline on the
 * strength of a probe it hasn't run yet.
 */
let probeVerdict: boolean | null = null;
/** In-flight probe, so N callers at once cost one request. */
let inFlight: Promise<boolean> | null = null;

/**
 * The probe target. `/api/health` is deliberately the cheapest thing we own:
 * public (middleware.ts PUBLIC_PATHS — so no session work), no database, 204.
 *
 * The service worker serves it NetworkOnly (app/sw.ts `healthProbeRoute`), which
 * is load-bearing — `defaultCache` would otherwise answer it from a NetworkFirst
 * cache for 24h and every probe would report a network that isn't there. The
 * cache-busting query is the second belt: in the window after a deploy an OLD
 * service worker still controls the page, and a unique URL misses its cache too.
 */
const PROBE_PATH = "/api/health";

/**
 * Long enough not to call a slow-but-working connection dead, short enough that
 * the user isn't staring at a frozen screen. Matches the service worker's
 * navigation timeout (app/sw.ts) and `hasServerSession()` — a connection that
 * cannot answer a 204 in 3s cannot serve this app either.
 */
const PROBE_TIMEOUT_MS = 3000;

/** Believed online: just confirming we still are. */
const POLL_ONLINE_MS = 30_000;
/** Believed offline: watching for the network to come back. Cheap — it fails locally. */
const POLL_OFFLINE_MS = 8_000;

function notifyAll(online: boolean) {
  for (const fn of listeners) {
    try {
      fn(online);
    } catch {
      // listener errors must not break other listeners
    }
  }
}

function navigatorOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

function init() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  /**
   * The interface came back. That is NOT proof the network did (a phone
   * reattaching to wifi with no route out fires this), so it clears nothing on
   * its own — it triggers a probe, and the probe decides. Listeners are still
   * notified, because `isOnline()` may already have changed: with a `null`
   * verdict, `navigator.onLine` IS the answer.
   */
  window.addEventListener("online", () => {
    notifyAll(isOnline());
    void probeConnectivity();
  });

  // The interface went away — the one case `navigator` is authoritative about.
  window.addEventListener("offline", () => notifyAll(isOnline()));
}

/** Synchronous best belief: cheap enough to call from a render path. */
export function isOnline(): boolean {
  if (!navigatorOnline()) return false;
  return probeVerdict !== false;
}

function setProbeVerdict(next: boolean) {
  const before = isOnline();
  probeVerdict = next;
  const after = isOnline();
  if (before !== after) notifyAll(after);
}

/**
 * Ask the network, don't guess. Resolves to the new verdict, and notifies
 * listeners if it changed.
 *
 * ANY HTTP response — 204, 404, even a 500 — counts as reachable: something
 * answered, so this device has a working path to a server. Only a thrown or
 * timed-out fetch means offline. The asymmetry is deliberate: a false "offline"
 * is far more damaging than a false "online" (it would switch a working app into
 * offline mode and ask for a PIN), and it stops a deployment that somehow lacks
 * `/api/health` from convincing every device it has no network.
 */
export async function probeConnectivity(): Promise<boolean> {
  if (typeof fetch === "undefined") return isOnline();
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      await fetch(`${PROBE_PATH}?t=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        // No cookies needed, and none wanted: this must stay the cheapest
        // request in the app.
        credentials: "omit",
        signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
      });
      setProbeVerdict(true);
      return true;
    } catch {
      setProbeVerdict(false);
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function onConnectivityChange(fn: ConnectivityListener): () => void {
  init();
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

/** Ref-counted, so a double mount (React strict mode) doesn't double the polling. */
let watchers = 0;
let stopWatch: (() => void) | null = null;

/**
 * Keep the verdict fresh. Mounted once for the whole app
 * (components/OfflineAutoSwitch.tsx); returns a teardown.
 *
 * Probes on every signal that the app may have missed a network change, in the
 * order it actually happens in the field:
 *
 * - the app returns to the foreground (`visibilitychange`, `pageshow`, `focus`,
 *   Capacitor's `resume`) — THE case: the user left for Settings, switched wifi
 *   off, and came back. No network event fires for that, because nothing changed
 *   while the WebView wasn't running;
 * - `online` (see `init()`) — the interface reattached;
 * - a timer, because a connection can die with no event at all.
 *
 * Polling pauses while the document is hidden: iOS suspends the WebView anyway,
 * and a probe fired as the app backgrounds would only resolve after the fact.
 */
export function startConnectivityWatch(): () => void {
  if (typeof window === "undefined") return () => {};

  watchers += 1;
  if (stopWatch) return releaseWatch;

  init();

  let timer: number | null = null;
  let disposed = false;

  const clear = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    if (disposed || timer !== null) return;
    const delay = isOnline() ? POLL_ONLINE_MS : POLL_OFFLINE_MS;
    timer = window.setTimeout(() => {
      timer = null;
      if (disposed) return;
      if (document.visibilityState === "visible") void probeConnectivity();
      schedule();
    }, delay);
  };

  const onForeground = () => {
    if (disposed) return;
    if (document.visibilityState !== "visible") {
      clear();
      return;
    }
    void probeConnectivity();
    // Re-arm at whatever interval the new verdict implies.
    clear();
    schedule();
  };

  document.addEventListener("visibilitychange", onForeground);
  window.addEventListener("pageshow", onForeground);
  window.addEventListener("focus", onForeground);

  // Capacitor fires `resume` when the native app comes back from the background.
  // visibilitychange usually covers it in WKWebView, but not on every iOS
  // version — and this is the exact scenario the whole module exists for.
  let removeResume: (() => void) | null = null;
  void (async () => {
    try {
      const { App } = await import("@capacitor/app");
      const handle = await App.addListener("resume", () => void probeConnectivity());
      if (disposed) void handle.remove();
      else removeResume = () => void handle.remove();
    } catch {
      // Not native / plugin unavailable — the DOM events above are enough.
    }
  })();

  if (document.visibilityState === "visible") void probeConnectivity();
  schedule();

  stopWatch = () => {
    disposed = true;
    clear();
    document.removeEventListener("visibilitychange", onForeground);
    window.removeEventListener("pageshow", onForeground);
    window.removeEventListener("focus", onForeground);
    removeResume?.();
  };

  return releaseWatch;
}

function releaseWatch() {
  watchers = Math.max(0, watchers - 1);
  if (watchers === 0 && stopWatch) {
    stopWatch();
    stopWatch = null;
  }
}

/** Test seam: forget the probe verdict, the listeners and any watch. */
export function __resetConnectivityForTests() {
  probeVerdict = null;
  inFlight = null;
  listeners = [];
  initialized = false;
  watchers = 0;
  stopWatch?.();
  stopWatch = null;
}
